import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ContentAttribute } from "JS/Models/Attributes";
import omit from "lodash-es/omit";

export enum AttributeType {
    AUDIO = "MP3",
    VIDEO = "Video",
}

export interface AttributesState {
    audio?: ContentTypeAttributes;
    video?: Omit<ContentTypeAttributes, "playlistSnackMsgCount">;
}

export interface ContentTypeAttributes {
    attributesList?: ContentAttribute[];
    isAttributeFetched?: boolean;
    uniqueSpeakers?: any[];
    contentSpeakers?: any[];
    speakers?: ContentAttribute[];
    selectedSpeakers?: string[];
    playlistSnackMsgCount?: number;
}

const initialStateData: ContentTypeAttributes = {
    attributesList: [],
    isAttributeFetched: false,
    uniqueSpeakers: [],
    contentSpeakers: [],
    speakers: [],
    selectedSpeakers: [],
    playlistSnackMsgCount: 0,
};

const initialState: AttributesState = {
    audio: initialStateData,
    video: omit(initialStateData, "playlistSnackMsgCount"),
};

export const attributesStateSlice = createSlice({
    name: "AttributesState",
    initialState,
    reducers: {
        setAudioSpeakers: (state, data: PayloadAction<ContentAttribute[]>) => ({
            ...state,
            audio: {
                ...state?.audio,
                isAttributeFetched: true,
                speakers: data.payload,
            },
        }),

        setAudioSelectedSpeakers: (state, data: PayloadAction<string[]>) => ({
            ...state,
            audio: {
                ...state?.audio,
                isAttributeFetched: true,
                selectedSpeakers: data.payload,
            },
        }),

        setVideoSpeakers: (state, data: PayloadAction<ContentAttribute[]>) => ({
            ...state,
            video: {
                ...state?.video,
                speakers: data.payload,
            },
        }),

        setVideoSelectedSpeakers: (state, data: PayloadAction<string[]>) => ({
            ...state,
            video: {
                ...state?.video,
                selectedSpeakers: data.payload,
            },
        }),

        resetAudioSelectedSpeakers: (state) => ({
            ...state,
            audio: { ...state?.audio, selectedSpeakers: [] },
        }),

        resetVideoSelectedSpeakers: (state) => ({
            ...state,
            video: { ...state?.video, selectedSpeakers: [] },
        }),

        resetOnlyAttributesState: (state: AttributesState) => {
            state = {
                ...initialState,
                audio: {
                    ...initialState?.audio,
                    selectedSpeakers: state?.audio?.selectedSpeakers,
                    playlistSnackMsgCount: state?.audio?.playlistSnackMsgCount,
                },
                video: {
                    ...initialState?.video,
                    selectedSpeakers: state?.video?.selectedSpeakers,
                },
            };
            return state;
        },

        setAudioSpeakerFetchStatus: (
            state,
            status: PayloadAction<boolean>,
        ) => ({
            ...state,
            audio: {
                ...state.audio,
                isAttributeFetched: status.payload,
            },
        }),

        setVideoSpeakerFetchStatus: (
            state,
            status: PayloadAction<boolean>,
        ) => ({
            ...state,
            video: {
                ...state.video,
                isAttributeFetched: status.payload,
            },
        }),

        incrementPlaylistSnackCount: (state) => ({
            ...state,
            audio: {
                ...state.audio,
                playlistSnackMsgCount:
                    (state.audio.playlistSnackMsgCount || 0) + 1,
            },
        }),

        resetAllAttributesState: () => initialState,
    },
});
export const {
    setAudioSpeakers,
    setVideoSpeakers,
    setAudioSelectedSpeakers,
    setVideoSelectedSpeakers,
    setAudioSpeakerFetchStatus,
    setVideoSpeakerFetchStatus,
    resetOnlyAttributesState,
    resetAllAttributesState,
    resetAudioSelectedSpeakers,
    resetVideoSelectedSpeakers,
    incrementPlaylistSnackCount,
} = attributesStateSlice.actions;
